import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic, Send, Sparkles, ArrowRight, Loader2, Volume2, VolumeX, Pause, Play,
  ShoppingBag, MapPin, X, ChevronRight, Info, Bike,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import CustomerTabBar from "../../components/CustomerTabBar";
import FumaCore from "../../components/ai/FumaCore";
import { useGeo, distanceKm, fmtDistance } from "../../lib/homeMode";
import { useFumaVoice } from "../../lib/tts";
import { tap } from "../../lib/native/haptics";
import { toast } from "sonner";

const ABOUT_TEXT =
  "Fuma c'anduma è il tuo assistente di quartiere. Puoi ordinare dalle attività locali, chiedere una consegna, trovare qualcosa vicino a te oppure semplicemente dirmi quello che ti serve. Tu chiedi, al resto pensiamo noi.";

const CHIPS = [
  { label: "🍕 Ho fame", prompt: "Vorrei ordinare del cibo vicino a me", testid: "chip-hungry" },
  { label: "🛵 Devo farmi portare qualcosa", prompt: "Ho bisogno di un rider per una consegna rapida", testid: "chip-delivery" },
  { label: "✨ Sorprendimi", prompt: "Consigliami qualcosa di speciale da fare o provare oggi", testid: "chip-surprise" },
];

const ACTIVE_STATUSES = ["in_attesa", "confermato", "accettato", "in_preparazione", "pronto", "rider_assegnato", "in_consegna", "ritirato"];
const newSid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function HomeSmart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const geo = useGeo();
  const voice = useFumaVoice();

  const [greet, setGreet] = useState({ name: "" });
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | analyzing | result
  const [result, setResult] = useState(null);
  const [coreMode, setCoreMode] = useState("idle");
  const [listening, setListening] = useState(false);
  const [about, setAbout] = useState(false);
  const [near, setNear] = useState([]);
  const [perTe, setPerTe] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);

  const recRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const sidRef = useRef(newSid());

  useEffect(() => { api.get("/fuma-ai/greeting").then(({ data }) => setGreet(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/public/map").then(({ data }) => {
      const pois = (data.pois || []).filter((p) => p.lat != null);
      setNear(pois.slice(0, 4));
      setPerTe(pois.slice(4, 7));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(({ data }) => {
      const act = (data || []).find((o) => ACTIVE_STATUSES.includes(o.status));
      setActiveOrder(act || null);
    }).catch(() => {});
  }, [user]);

  // Prefill da altre schermate (es. ricerca) → invia in automatico
  useEffect(() => {
    let pre = null;
    try { pre = sessionStorage.getItem("fuma_prefill"); sessionStorage.removeItem("fuma_prefill"); } catch (e) {}
    if (pre) { setInput(pre); setTimeout(() => submit(pre), 300); }
    // eslint-disable-next-line
  }, []);

  // Mini-presentazione automatica alla prima apertura — SOLO testo, nessun audio
  useEffect(() => {
    try {
      if (!localStorage.getItem("fuma_about_seen")) {
        setAbout(true);
        localStorage.setItem("fuma_about_seen", "1");
      }
    } catch (e) {}
  }, []);

  // Riconoscimento vocale (Web Speech) con fallback MediaRecorder + Whisper
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "it-IT"; r.interimResults = false; r.maxAlternatives = 1;
    r.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); setListening(false); submit(t); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recRef.current = r;
    return () => { try { r.abort(); } catch (e) {} };
    // eslint-disable-next-line
  }, []);

  useEffect(() => () => voice.stop(), []); // eslint-disable-line

  const toggleMic = async () => {
    tap("light");
    if (recRef.current) {
      if (listening) { recRef.current.stop(); setListening(false); return; }
      try { recRef.current.start(); setListening(true); setCoreMode("listening"); } catch (e) {}
      return;
    }
    if (listening && mediaRef.current) { try { mediaRef.current.stop(); } catch (e) {} return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false); setCoreMode("idle");
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "audio.webm"); fd.append("language", "it");
        try {
          const { data } = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) { setInput(data.text); submit(data.text); } else toast("Non ho capito, riprova 🎙️");
        } catch (e) { toast("Trascrizione non riuscita"); }
      };
      mr.start(); setListening(true); setCoreMode("listening");
      setTimeout(() => { if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop(); }, 6000);
    } catch (e) { toast("Microfono non disponibile su questo dispositivo"); }
  };

  const submit = async (msg) => {
    if (phase === "analyzing") return;
    const message = (typeof msg === "string" ? msg : input).trim();
    if (!message) return;
    setInput(""); setAbout(false); voice.stop();
    setPhase("analyzing"); setResult(null); setCoreMode("processing");
    const started = Date.now();
    let data;
    try {
      const res = await api.post("/fuma-ai/plan", { message, session_id: sidRef.current });
      data = res.data;
      if (data.session_id) sidRef.current = data.session_id;
    } catch (e) {
      data = { reply: "Non riesco a raggiungere Fuma adesso, ma puoi comunque procedere 👇", proposal: null, businesses: [], actions: [], chips: null };
    }
    const wait = Math.max(0, 1200 - (Date.now() - started));
    setTimeout(() => {
      setResult(data); setPhase("result"); setCoreMode("idle");
    }, wait);
  };

  const reset = () => { voice.stop(); sidRef.current = newSid(); setPhase("idle"); setResult(null); setInput(""); setCoreMode("idle"); };
  const askAbout = () => { setAbout(true); reset(); };

  const VoiceBtn = ({ text, testid }) => {
    if (voice.muted) return null;
    const playing = voice.playing && voice.state === "playing";
    return (
      <button
        data-testid={testid || "fuma-tts-toggle-btn"}
        onClick={() => voice.speak(text)}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#00E676]/15 text-[#00E676] text-xs font-semibold px-3 py-1.5 active:scale-95 transition-transform shrink-0"
      >
        {voice.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        {playing ? "Pausa" : "Ascolta"}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-28" data-testid="home-smart">
      <div className="max-w-md mx-auto px-5">
        {/* Header brand */}
        <div className="flex items-center justify-between pt-5" data-testid="home-header">
          <div>
            <div className="font-['Unbounded'] font-black text-2xl tracking-tight leading-none">Fuma c'anduma</div>
            <div className="text-xs text-white/40 mt-1 tracking-wide">L'amichevole rider di quartiere</div>
          </div>
          <button
            data-testid="fuma-voice-mute"
            onClick={voice.toggleMute}
            title={voice.muted ? "Attiva voce" : "Disattiva voce"}
            className={`h-9 w-9 rounded-full flex items-center justify-center border ${voice.muted ? "border-white/10 text-white/40" : "border-[#00E676]/40 text-[#00E676]"}`}
          >
            {voice.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        {/* HERO Fuma AI */}
        <div className="flex flex-col items-center mt-6">
          <FumaCore mode={coreMode} />
          <p className="mt-5 font-['Unbounded'] font-bold text-xl text-center leading-tight" data-testid="fuma-greeting">
            Ciao {greet.name ? `${greet.name} ` : ""}<span>👋</span> Sono <span className="text-[#00E676]">Fuma</span>.
          </p>
          <p className="text-sm text-white/55 mt-1.5 text-center max-w-[18rem]">Dimmi cosa ti serve e ti aiuto io.</p>
        </div>

        {/* Barra input grande */}
        <div className="mt-6 flex items-center gap-2 rounded-2xl bg-[#121215] border border-[#00E676]/25 p-2 shadow-[0_0_30px_rgba(0,230,118,.08)]" data-testid="fuma-ai-input-bar">
          <button
            data-testid="fuma-ai-mic-btn"
            onClick={toggleMic}
            className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${listening ? "bg-[#00E676] text-black animate-pulse" : "text-[#00E676] hover:bg-white/5"}`}
          >
            <Mic className="h-5 w-5" />
          </button>
          <input
            data-testid="fuma-ai-input"
            value={input}
            disabled={phase === "analyzing"}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="Chiedimi qualsiasi cosa…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30 px-1 disabled:opacity-50"
          />
          <button
            data-testid="fuma-ai-send-btn"
            disabled={phase === "analyzing"}
            onClick={() => submit()}
            className="h-11 w-11 rounded-xl bg-[#00E676] text-black flex items-center justify-center shrink-0 active:scale-95 disabled:opacity-50"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>

        {/* Suggerimenti rapidi + Che cos'è Fuma */}
        {phase === "idle" && (
          <div className="mt-4 space-y-2" data-testid="fuma-quick-chips">
            {CHIPS.map((c) => (
              <button
                key={c.testid}
                data-testid={c.testid}
                onClick={() => submit(c.prompt)}
                className="w-full flex items-center justify-between rounded-2xl bg-[#121215] border border-white/8 px-4 py-3 text-left hover:border-[#00E676]/30 transition-colors active:scale-[0.99]"
              >
                <span className="text-sm text-white/85">{c.label}</span>
                <ArrowRight className="h-4 w-4 text-white/25" />
              </button>
            ))}
            <button
              data-testid="btn-about-fuma-inline"
              onClick={askAbout}
              className="w-full flex items-center justify-center gap-2 mt-1 text-sm text-[#7DF9FF]/80 hover:text-[#7DF9FF] py-1.5"
            >
              <Info className="h-4 w-4" /> Che cos'è Fuma c'anduma?
            </button>
          </div>
        )}

        {/* Risposta inline "Che cos'è Fuma" */}
        {about && phase === "idle" && (
          <div className="mt-3 rounded-2xl bg-[#121215] border border-[#00E676]/25 p-4 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="fuma-about">
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-full bg-[#00E676]/15 flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4 text-[#00E676]" /></div>
              <p className="text-sm text-white/85 leading-relaxed flex-1">{ABOUT_TEXT}</p>
              <button data-testid="fuma-about-close" onClick={() => setAbout(false)} className="text-white/30 hover:text-white/60 shrink-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex justify-end"><VoiceBtn text={ABOUT_TEXT} testid="fuma-about-listen" /></div>
          </div>
        )}

        {/* Analisi */}
        {phase === "analyzing" && (
          <div className="mt-5 flex items-center gap-2 text-sm text-[#00E676] justify-center" data-testid="fuma-analyzing">
            <Loader2 className="h-4 w-4 animate-spin" /> Sto pensando a qualcosa per te…
          </div>
        )}

        {/* Risultato conversazione */}
        {phase === "result" && result && (
          <div className="mt-5 space-y-4" data-testid="fuma-result">
            <div className="flex items-start gap-2.5" data-testid="fuma-reply">
              <div className="h-8 w-8 rounded-full bg-[#00E676]/15 flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4 text-[#00E676]" /></div>
              <div className="rounded-2xl rounded-tl-sm bg-[#121215] border border-white/10 px-3.5 py-2.5 text-sm text-white/85 flex-1">{result.reply}</div>
            </div>
            {!voice.muted && (result.narration || result.reply) && (
              <div className="flex justify-end -mt-1"><VoiceBtn text={result.narration || result.reply} testid="fuma-result-listen" /></div>
            )}

            {result.chips?.options?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="fuma-chips">
                {result.chips.options.map((o, i) => (
                  <button key={i} data-testid={`fuma-chip-${i}`} onClick={() => submit(o.text)} className="rounded-full border border-[#00E676]/40 text-[#00E676] px-3.5 py-1.5 text-sm hover:bg-[#00E676]/10 transition-colors">{o.label}</button>
                ))}
              </div>
            )}

            {result.proposal?.steps?.length > 0 && (
              <div className="rounded-2xl bg-[#121215] border border-[#00E676]/25 p-4" data-testid="fuma-proposal">
                <div className="font-semibold text-base">{result.proposal.title}</div>
                <div className="mt-3 space-y-2">
                  {result.proposal.steps.map((s, i) => (
                    <div key={i} data-testid={`fuma-step-${i}`} className="flex items-center gap-3 rounded-xl bg-[#1A1A1E] border border-white/10 p-2.5">
                      <div className="h-7 w-7 rounded-full bg-[#00E676]/15 text-[#00E676] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                      <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{s.label}</div><div className="text-[11px] text-white/40 truncate">{s.business_name} · {s.time}</div></div>
                    </div>
                  ))}
                </div>
                <button data-testid="fuma-live-plan" onClick={() => navigate("/esplora/mappa?plan=1")} className="mt-4 w-full flex items-center justify-center gap-1.5 rounded-full bg-[#00E676] text-black font-semibold py-2.5 text-sm active:scale-95"><MapPin className="h-4 w-4" /> Vivi questa esperienza</button>
              </div>
            )}

            {result.businesses?.length > 0 && (
              <div className="space-y-2" data-testid="fuma-businesses">
                {result.businesses.slice(0, 5).map((b) => (
                  <button key={b.id} data-testid={`fuma-biz-${b.id}`} onClick={() => navigate(`/attivita/${b.id}`)} className="w-full text-left flex items-center gap-3 rounded-2xl bg-[#121215] border border-white/10 hover:border-[#00E676]/40 p-3 transition-colors active:scale-[0.99]">
                    <div className="h-12 w-12 rounded-xl bg-[#1A1A1E] overflow-hidden flex items-center justify-center shrink-0">
                      {b.image ? <img src={b.image} alt={b.name} className="h-full w-full object-cover" /> : <ShoppingBag className="h-5 w-5 text-white/30" />}
                    </div>
                    <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{b.name}</div><div className="text-[11px] text-white/40 truncate">{b.category}{b.has_offer ? ` · ✦ ${b.offer_title}` : ""}</div></div>
                    <ArrowRight className="h-4 w-4 text-white/30 shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {result.actions?.map((a, i) => (
              <button key={i} data-testid={`fuma-action-${i}`} onClick={() => navigate(a.to)} className="w-full rounded-full border border-white/15 py-2.5 text-sm text-white/80 hover:border-[#00E676]/40 transition-colors">{a.label}</button>
            ))}

            <button data-testid="fuma-new" onClick={reset} className="w-full text-center text-xs text-white/40 pt-1">↺ Nuova richiesta</button>
          </div>
        )}

        {/* Sezioni contestuali (solo in idle) */}
        {phase === "idle" && (
          <div className="mt-8 space-y-8">
            {/* Ordine attivo — solo se esiste */}
            {activeOrder && (
              <section data-testid="section-ordine-attivo">
                <h2 className="font-['Unbounded'] font-bold text-lg mb-3">Ordine <span className="text-[#00E676]">attivo</span></h2>
                <button
                  data-testid="active-order-card"
                  onClick={() => navigate(`/app/orders/${activeOrder.id}`)}
                  className="w-full text-left rounded-2xl bg-[#121215] border border-[#00E676]/25 p-4 flex items-center gap-3 active:scale-[0.99] transition-transform"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#00E676]/15 flex items-center justify-center shrink-0"><Bike className="h-5 w-5 text-[#00E676]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{activeOrder.business_name || "Il tuo ordine"}</div>
                    <div className="text-[11px] text-[#00E676] mt-0.5 capitalize">{String(activeOrder.status || "").replace(/_/g, " ")}</div>
                  </div>
                  <span className="text-xs text-[#00E676] font-semibold flex items-center gap-1">Segui <ChevronRight className="h-4 w-4" /></span>
                </button>
              </section>
            )}

            {/* Vicino a te */}
            {near.length > 0 && (
              <section data-testid="section-vicino-a-te">
                <h2 className="font-['Unbounded'] font-bold text-lg">Vicino a <span className="text-[#00E676]">te</span></h2>
                <p className="text-xs text-white/40 mt-0.5 mb-3">Fuma pensa che potrebbero interessarti…</p>
                <div className="space-y-2">
                  {near.map((p) => {
                    const d = geo ? fmtDistance(distanceKm(geo, { lat: p.lat, lng: p.lng })) : null;
                    return (
                      <button key={p.id} data-testid={`near-item-${p.id}`} onClick={() => navigate(`/attivita/${p.id}`)} className="w-full text-left flex items-center gap-3 rounded-2xl bg-[#121215] border border-white/5 hover:border-[#00E676]/30 p-3 transition-colors active:scale-[0.99]">
                        <div className="h-12 w-12 rounded-xl bg-[#1A1A1E] overflow-hidden flex items-center justify-center shrink-0">
                          {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <ShoppingBag className="h-5 w-5 text-white/25" />}
                        </div>
                        <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{p.name}</div><div className="text-[11px] text-white/40 truncate">{p.category}{d ? ` · ${d}` : ""}</div></div>
                        <ChevronRight className="h-4 w-4 text-white/20 shrink-0" />
                      </button>
                    );
                  })}
                </div>
                <button data-testid="near-see-more" onClick={() => navigate("/esplora")} className="mt-3 w-full text-center text-sm text-[#00E676] font-medium">Vedi altri consigli di Fuma</button>
              </section>
            )}

            {/* Per te */}
            {perTe.length > 0 && (
              <section data-testid="section-per-te">
                <h2 className="font-['Unbounded'] font-bold text-lg">Per <span className="text-[#00E676]">te</span></h2>
                <p className="text-xs text-white/40 mt-0.5 mb-3">Suggerimenti su misura</p>
                <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-2 snap-x">
                  {perTe.map((p) => (
                    <button key={p.id} data-testid={`perte-item-${p.id}`} onClick={() => navigate(`/attivita/${p.id}`)} className="shrink-0 w-40 text-left rounded-2xl bg-[#121215] border border-white/5 overflow-hidden active:scale-[0.98] transition-transform snap-start">
                      <div className="h-24 bg-[#1A1A1E]">{p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><ShoppingBag className="h-6 w-6 text-white/20" /></div>}</div>
                      <div className="p-2.5"><div className="font-semibold text-sm truncate">{p.name}</div><div className="text-[11px] text-white/40 truncate">{p.category}</div></div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      <CustomerTabBar active="home" />
    </div>
  );
}
